/**
 * Grouping items by when they happened. Generic over `DateGroupable`, so it names nothing from
 * `CONTEXT.md` and satisfies Rule L; it came from `utils/date-grouping.ts` on step 9 (#415).
 *
 * `groupByDate` came with it and was deleted rather than moved — zero callers, the outcome
 * `placement-drawers.md:63` records for it.
 */

import { format, isToday, isYesterday, isThisWeek, isThisMonth } from 'date-fns'

export interface DateGroupable {
  timestamp: Date
}

export interface DateGroup<T> {
  date: string // yyyy-MM-dd format
  displayLabel: string // "Today", "Yesterday", or "Monday, January 21, 2026"
  entries: T[]
}

/**
 * Temporal range keys in display order.
 * Used as both identifiers and sort keys.
 */
const TEMPORAL_RANGES = ['today', 'this-week', 'this-month', 'older'] as const

const TEMPORAL_LABELS: Record<(typeof TEMPORAL_RANGES)[number], string> = {
  today: 'Today',
  'this-week': 'This Week',
  'this-month': 'This Month',
  older: 'Older',
}

/**
 * Groups items by temporal range: Today, This Week, This Month, Older.
 *
 * - Week starts on Monday (ISO 8601 / Phase 21 decision)
 * - Entries are sorted descending (newest first) within each group
 * - Empty groups are excluded from the result
 *
 * @param items - Array of items with timestamp property
 * @returns Array of temporal range groups in fixed order (Today → Older)
 */
export function groupByTemporalRange<T extends DateGroupable>(
  items: T[],
): DateGroup<T>[] {
  // Bucket entries into temporal ranges
  const buckets: Record<(typeof TEMPORAL_RANGES)[number], T[]> = {
    today: [],
    'this-week': [],
    'this-month': [],
    older: [],
  }

  for (const item of items) {
    const d = item.timestamp
    if (isToday(d)) {
      buckets.today.push(item)
    } else if (isThisWeek(d, { weekStartsOn: 1 })) {
      buckets['this-week'].push(item)
    } else if (isThisMonth(d)) {
      buckets['this-month'].push(item)
    } else {
      buckets.older.push(item)
    }
  }

  // Build groups in fixed order, skipping empty buckets
  const groups: DateGroup<T>[] = []

  for (const key of TEMPORAL_RANGES) {
    const entries = buckets[key]
    if (entries.length === 0) continue

    // Sort entries descending within group (newest first)
    entries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

    groups.push({
      date: key,
      displayLabel: TEMPORAL_LABELS[key],
      entries,
    })
  }

  return groups
}
